import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title: string;
  description?: string;
  canonicalUrl?: string;
  imageUrl?: string;
  type?: 'website' | 'article' | 'profile';
  structuredData?: Record<string, unknown>;
  appendBrandName?: boolean;
}

/**
 * Centralized component for handling SEO metadata across the application
 */
const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  canonicalUrl,
  imageUrl,
  type = 'website',
  structuredData,
  appendBrandName = true,
}) => {
  // Ensure title has the site name at the end if not already present
  const formattedTitle = appendBrandName && !title.includes('World Class Design') 
    ? `${title} | World Class Design`
    : title;
  
  // Use current URL as canonical if not specified
  const canonical = canonicalUrl || window.location.href;
  
  // Default image if none provided (uses the new image in /public)
  const defaultImage = '/world_class_design_open_graph.png';
  const ogImage = imageUrl || defaultImage;

  // Add version parameter for cache busting (only for favicons and non-social media resources)
  const addVersionParam = (url: string) => {
    if (url.startsWith('http')) return url; // Don't add to external URLs
    const version = import.meta.env.VITE_APP_VERSION || '1.1';
    return `${url}?v=${version}`;
  };

  // Ensure image URL is absolute for social media platforms
  // IMPORTANT: Do NOT add version parameters to social media images
  const getAbsoluteImageUrl = (url: string, forSocialMedia: boolean = false) => {
    if (url.startsWith('http')) return url;
    if (url.startsWith('/')) {
      const baseUrl = `${window.location.origin}${url}`;
      // Only add version parameter for non-social media resources
      return forSocialMedia ? baseUrl : addVersionParam(baseUrl);
    }
    const baseUrl = `${window.location.origin}/${url}`;
    return forSocialMedia ? baseUrl : addVersionParam(baseUrl);
  };

  // Get absolute image URL for social media (without version parameters)
  const socialMediaImageUrl = getAbsoluteImageUrl(ogImage, true);
  
  // Get absolute image URL for other resources (with version parameters)
  const absoluteImageUrl = getAbsoluteImageUrl(ogImage, false);

  // Dynamically determine image type based on file extension
  const getImageType = (url: string): string => {
    const extension = url.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'webp':
        return 'image/webp';
      case 'gif':
        return 'image/gif';
      default:
        return 'image/jpeg'; // Default fallback
    }
  };

  const imageType = getImageType(socialMediaImageUrl);

  // Debug logging for social media image processing
  console.log('[SEOHead] Social media image processing:', {
    originalImageUrl: imageUrl,
    ogImage,
    socialMediaImageUrl,
    imageType,
    isDefaultImage: ogImage === defaultImage
  });

  return (
    <Helmet>
      {/* Basic metadata */}
      <title>{formattedTitle}</title>
      {description && <meta name="description" content={description} />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Favicons - standardized across the site with cache busting */}
      <link rel="icon" href={addVersionParam('/favicon.png')} type="image/png" />
      <link rel="apple-touch-icon" href={addVersionParam('/favicon.png')} />
      <link rel="shortcut icon" href={addVersionParam('/favicon.png')} type="image/png" />
      
      {/* Open Graph metadata - using clean URLs without version parameters */}
      <meta property="og:title" content={formattedTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={socialMediaImageUrl} />
      {/* Use dynamic image type instead of hardcoded JPEG */}
      <meta property="og:image:type" content={imageType} />
      <meta property="og:site_name" content="World Class Design" />
      <meta property="og:locale" content="en_US" />
      
      {/* Twitter metadata - using clean URLs without version parameters */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={formattedTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={socialMediaImageUrl} />
      <meta name="twitter:image:alt" content={`${formattedTitle} - World Class Design`} />
      
      {/* Additional social media meta tags */}
      <meta name="twitter:site" content="@africanswhodesign" />
      <meta name="twitter:creator" content="@africanswhodesign" />
      
      {/* LinkedIn specific meta tags - using clean URLs */}
      <meta property="og:image:secure_url" content={socialMediaImageUrl} />
      <meta property="og:image:alt" content={`${formattedTitle} - World Class Design`} />
      
      {/* Additional Open Graph tags for better social media sharing */}
      <meta property="og:determiner" content="the" />
      <meta property="og:updated_time" content={new Date().toISOString()} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
